import { API_BASE_URL } from "../../constant/constant";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IntroductionContainer,
  ContentWrapper,
  TextSection,
  Heading,
  IntroTxt,
  NextButton,
  ImageSection,
  LaptopImage,
  LogoContainer,
} from "./style";
import {
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import LaptopImg from "../../assets/introduction.jpg";
import LogoImg from "../../assets/Logo.png";
import onboardingImg from "../../assets/onboarding.png";
import OnboardingVideo from "../../assets/Aivoria AA (Onboarding video).mp4";
import { useTranslation } from "react-i18next";

const Introduction = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [profession, setProfession] = useState("");
  const [customProfession, setCustomProfession] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { t } = useTranslation();

  const handleNextClick = () => {
    if (step === 1) {
      setStep(2);
    } else {
      // For step 2, validate profession selection
      if (!profession) {
        setError(true);
        return;
      }

      // Check if custom profession is selected but no value is entered
      if (profession === "Custom" && !customProfession) {
        setError(true);
        return;
      }

      // For step 2, handle the API call
      const selectedProfession =
        profession === "Custom" ? customProfession : profession;
      const userId = localStorage.getItem("id");

      if (!userId) {
        console.error("Missing userId");
        return;
      }

      setLoading(true);

      fetch(`${API_BASE_URL}/api/auth/update-profession`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userId,
          profession: selectedProfession,
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          console.log("Success:", data.message);
          navigate("/avatar-selection");
        })
        .catch((error) => {
          console.error("Error:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleSkip = () => {
    navigate("/avatar-selection");
  };

  const handleProfessionChange = (event) => {
    setProfession(event.target.value);
    setError(false); // Clear error when user makes a selection
  };

  const handleCustomProfessionChange = (event) => {
    setCustomProfession(event.target.value);
    if (event.target.value) {
      setError(false); // Clear error when user enters text
    }
  };

  const renderStep1 = () => {
    return (
      <>
        <Heading>{t("introduction.title")}</Heading>
        <IntroTxt>{t("introduction.description")}</IntroTxt>
        <NextButton
          onClick={handleNextClick}
          style={{ marginTop: "3rem", width: "max-content" }}
        >
          {t("steps.next")}
        </NextButton>
      </>
    );
  };

  const renderStep2 = () => {
    return (
      <>
        <Heading>{t("steps.title")}</Heading>
        <IntroTxt>
          {t("steps.description")}
        </IntroTxt>

        <FormControl
          fullWidth
          style={{ marginTop: "2rem", width: "80%" }}
          error={error}
        >
          <Select
            value={profession}
            onChange={handleProfessionChange}
            displayEmpty
            style={{ marginBottom: "1rem" }}
            sx={{
              borderRadius: "0.5rem",
              borderColor: error ? "red" : undefined,
            }}
          >
            <MenuItem value="" disabled>
              {t("steps.selectProfession")}
            </MenuItem>
            <MenuItem value="HR">HR</MenuItem>
            <MenuItem value="Personal Assistant">{t("steps.professions.personalAssistant")}</MenuItem>
            <MenuItem value="Marketing">Marketing</MenuItem>
            <MenuItem value="Sales">Sales</MenuItem>
            <MenuItem value="Custom">Custom</MenuItem>
          </Select>
          {error && !profession && (
            <FormHelperText error>{t("steps.errors.selectProfession")}</FormHelperText>
          )}

          {profession === "Custom" && (
            <TextField
              fullWidth
              placeholder={t("steps.enterProfession")}
              value={customProfession}
              onChange={handleCustomProfessionChange}
              style={{ marginTop: "1rem" }}
              error={error && !customProfession}
              helperText={
                error && !customProfession ? t("steps.errors.enterProfession") : ""
              }
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: "0.5rem",
                },
              }}
            />
          )}
        </FormControl>

        <div style={{ display: "flex", marginTop: "3rem" }}>
          <NextButton
            onClick={handleSkip}
            style={{
              width: "max-content",
              color: "#696974",
              background: "#FFF",
            }}
          >
            {t("steps.skip")}
          </NextButton>
          <NextButton
            onClick={handleNextClick}
            style={{ width: "max-content" }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t("steps.next")}
          </NextButton>
        </div>
      </>
    );
  };

  // Add styles for transitions
  const transitionStyles = `
    .fade-enter {
      opacity: 0;
      transform: translateY(20px);
    }
    .fade-enter-active {
      opacity: 1;
      transform: translateY(0);
      transition: opacity 400ms, transform 400ms;
    }
    .fade-exit {
      opacity: 1;
      transform: translateY(0);
    }
    .fade-exit-active {
      opacity: 0;
      transform: translateY(-20px);
      transition: opacity 400ms, transform 400ms;
    }
  `;

  return (
    <IntroductionContainer>
      <style>{transitionStyles}</style>

      <ContentWrapper>
        <TextSection>
          <LogoContainer>
            <img
              src={LogoImg}
              alt="AI Flow Pro Logo"
            />
          </LogoContainer>
          <SwitchTransition mode="out-in">
            <CSSTransition
              key={step}
              addEndListener={(node, done) => {
                node.addEventListener("transitionend", done, false);
              }}
              classNames="fade"
            >
              <div>{step === 1 ? renderStep1() : renderStep2()}</div>
            </CSSTransition>
          </SwitchTransition>
        </TextSection>
        <ImageSection>
          <img src={onboardingImg} alt="Aivoria Dashboard" />
        </ImageSection>
      </ContentWrapper>
    </IntroductionContainer>
  );
};

export default Introduction;
